import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PackageSearch, BadgePercent } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { FadeUp } from "../components/Reveal";

const SORT_OPTIONS = [
  { value: "popular", label: "Cele mai populare" },
  { value: "price_asc", label: "Preț: crescător" },
  { value: "price_desc", label: "Preț: descrescător" },
  { value: "new", label: "Noutăți" },
];

const DEFAULT_AROME_NAMES = ["Ocean Breeze", "Floral Fresh"];
const AROMA_CATEGORIES = ["Detergent", "Balsam"];

// teme vizuale per categorie pentru banda magazinului
const CATEGORY_THEMES = {
  "Detergent": { from: "#0e4f8a", to: "#0a3a66" },
  "Balsam": { from: "#be3e7f", to: "#8e2b60" },
  "Detergent vase": { from: "#0e7a5f", to: "#0a5c47" },
  "Săpun lichid": { from: "#0e7490", to: "#155e75" },
  "Produse curățenie": { from: "#5b4b8a", to: "#43356b" },
};
const PROMO_THEME = { from: "#b45309", to: "#92400e" };
const DEFAULT_THEME = { from: "#1B4B7A", to: "#123a5f" };

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categorie = searchParams.get("categorie") || "";
  const aroma = searchParams.get("aroma") || "";
  const promo = searchParams.get("promo") || "";
  const [sort, setSort] = useState("popular");
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [arome, setArome] = useState(DEFAULT_AROME_NAMES);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/content").then((r) => {
      const names = (r.data.arome || []).map((a) => a.name).filter(Boolean);
      if (names.length) setArome(names);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setProducts(null);
    const params = { sort };
    if (categorie) params.category = categorie;
    if (aroma) params.aroma = aroma;
    if (promo) params.promo = true;
    api.get("/products", { params }).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [categorie, aroma, promo, sort]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const chipCls = (active) =>
    `shrink-0 whitespace-nowrap rounded-full px-6 py-3.5 text-base font-bold transition-all ${active ? "bg-navy text-white shadow-md shadow-navy/20" : "bg-white text-muted2 hover:bg-skytint hover:text-navy border border-line"}`;

  const title = promo ? "Promoții" : categorie || "Toate produsele";
  const activeCat = categories.find((c) => c.name === categorie);
  const theme = promo ? PROMO_THEME : (CATEGORY_THEMES[categorie] || DEFAULT_THEME);
  const bandEyebrow = promo ? "Oferte cu reducere" : activeCat?.tagline || "Magazin";
  const bandSubtitle = promo
    ? "Ofertele momentului, cu stoc limitat. Plătești ramburs, la livrare."
    : activeCat?.description || "Dozare simplă, preț corect per spălare. Plătești ramburs, la livrare.";

  return (
    <main data-testid="shop-page">
      <section
        className="relative overflow-hidden py-9 transition-all duration-700"
        style={{ background: `linear-gradient(100deg, ${theme.from} 0%, ${theme.to} 100%)` }}
        data-testid="shop-hero-band"
      >
        {/* poza categoriei, estompată spre stânga */}
        {activeCat?.image && !promo && (
          <img
            key={activeCat.id}
            src={activeCat.image}
            alt=""
            className="animate-fade-in absolute inset-y-0 right-0 h-full w-3/5 object-cover opacity-40 [mask-image:linear-gradient(90deg,transparent,black_70%)]"
            data-testid="shop-hero-image"
          />
        )}
        {/* bule de săpun — element de design */}
        <div className="pointer-events-none absolute -right-10 -top-16 h-56 w-56 rounded-full border-[3px] border-white/10" />
        <div className="pointer-events-none absolute right-52 top-4 h-16 w-16 rounded-full border-2 border-white/15" />
        <div className="pointer-events-none absolute right-16 top-10 h-6 w-6 rounded-full bg-white/10" />
        <div className="pointer-events-none absolute -left-14 -bottom-20 h-48 w-48 rounded-full border-[3px] border-white/5" />
        <div className="relative mx-auto max-w-[1120px] px-5 sm:px-8">
          <FadeUp key={promo ? "promo" : categorie || "toate"}>
            <span className="text-xs font-bold uppercase tracking-[0.08em] text-white/70">
              {bandEyebrow}
            </span>
            <h1 className="mt-1.5 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="shop-title">
              {title}
            </h1>
            <p className="mt-1.5 max-w-xl text-sm text-white/80" data-testid="shop-subtitle">
              {bandSubtitle}
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-[1120px] px-5 py-12 sm:px-8">
        <div className="flex flex-nowrap items-center gap-2.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="category-filters">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setParam("categorie", c.name)}
              className={chipCls(categorie === c.name)}
              data-testid={`filter-category-${c.id}`}
            >
              {c.name}
            </button>
          ))}
          <button
            onClick={() => setParam("promo", promo ? "" : "1")}
            className={`flex items-center gap-1.5 ${chipCls(!!promo)}`}
            data-testid="filter-promo"
          >
            <BadgePercent className="h-4 w-4" />
            Promoții
          </button>
          <button onClick={() => setSearchParams({})} className={chipCls(!categorie && !aroma && !promo)} data-testid="filter-category-all">
            Toate
          </button>
        </div>

        <div className="mt-5 flex items-center justify-end">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-full border border-line bg-white px-4 py-2.5 text-sm font-semibold text-ink outline-none focus:border-sky2"
            data-testid="shop-sort-select"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {AROMA_CATEGORIES.includes(categorie) && (
          <div className="mt-4 flex flex-wrap items-center gap-2.5" data-testid="aroma-filters">
            <span className="text-sm font-bold uppercase tracking-[0.08em] text-muted2">Aromă:</span>
            <button onClick={() => setParam("aroma", "")} className={chipCls(!aroma)} data-testid="filter-aroma-all">
              Toate
            </button>
            {arome.map((a) => (
              <button
                key={a}
                onClick={() => setParam("aroma", a)}
                className={chipCls(aroma === a)}
                data-testid={`filter-aroma-${a.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {a}
              </button>
            ))}
          </div>
        )}

        {activeCat?.usage && (
          <div className="mt-6 rounded-2xl border border-line bg-skytint/60 p-6" data-testid="category-usage">
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-navy">Instrucțiuni de folosire</p>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-ink">{activeCat.usage}</p>
          </div>
        )}

        {products === null ? (
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-96 animate-pulse rounded-lg bg-skytint" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="mt-16 flex flex-col items-center gap-4 py-16 text-center" data-testid="shop-empty-state">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-skytint">
              <PackageSearch className="h-7 w-7 text-navy" />
            </span>
            <p className="font-display text-lg font-bold text-navy">Niciun produs găsit</p>
            <p className="max-w-sm text-sm text-muted2">Încearcă alt termen de căutare sau alege alt filtru.</p>
          </div>
        ) : (
          <>
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.08em] text-muted2" data-testid="shop-results-count">
              {products.length} produse
            </p>
            <div className="mt-4 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
